import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { savingLabel } from "@shared-core/domain/quantityLadder";
import type { CustomerQuantityLadderRow } from "@shared-core/data/customer-api-types";
import { Badge } from "../../../app/components/primitives/badge";
import {
  RadioGroup,
  RadioGroupTileItem,
} from "../../../app/components/primitives/radio-group";
import { presentLadderRows } from "../QuantityLadder";

// The item page's quantity choice: QuantityLadder's rows in the same order and
// with the same content, made vertically small (Brad, 2026-10-12: the page was
// too long). One line per quantity, about 38px: the quantity (and Best value)
// on the left, the saving, total and price each on the right, wrapping to a
// tight second line only where the buy box is too narrow for one. One
// radiogroup, so it is a drop-in for QuantityLadder's props and can be swapped
// for a dropdown by replacing this one component.
export function CompactQuantityLadder({
  rows,
  quantities,
  selectedQuantity,
  onSelect,
}: {
  rows: CustomerQuantityLadderRow[];
  /** Fallback when `rows` hasn't arrived yet, or pricing failed: the quantities stay selectable, unpriced. */
  quantities: number[];
  selectedQuantity: number | null;
  onSelect: (quantity: number) => void;
}) {
  const presented = presentLadderRows(rows, quantities, selectedQuantity);

  return (
    <RadioGroup
      value={selectedQuantity !== null ? String(selectedQuantity) : ""}
      onValueChange={(value) => onSelect(Number(value))}
      aria-label="Quantity"
      // Pin the column to the container: an implicit `auto` column would grow to the widest tile.
      className="grid-cols-[minmax(0,1fr)] gap-1.5"
    >
      {presented.map((row) => (
        <RadioGroupTileItem
          key={row.quantity}
          value={String(row.quantity)}
          disabled={!row.available}
          className="flex min-h-9 w-full min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-0 rounded-lg px-3 py-1 text-left font-normal"
        >
          <span className="flex items-center gap-x-2">
            <span className="font-semibold tabular-nums">
              {row.quantity.toLocaleString("en-GB")}
            </span>
            {row.isBestValue && (
              <Badge variant="success" className="px-1.5 py-0 text-[10px]">
                Best value
              </Badge>
            )}
          </span>
          {row.available ? (
            <span className="flex flex-wrap items-baseline justify-end gap-x-2">
              {savingLabel(row.savingPct) && (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  {savingLabel(row.savingPct)}
                </Badge>
              )}
              <span className="font-semibold tabular-nums">
                {formatQuotedPrice(row.totalPence ?? 0)}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatQuotedPrice(row.unitPricePence ?? 0)}/unit
              </span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              Price on request
            </span>
          )}
        </RadioGroupTileItem>
      ))}
    </RadioGroup>
  );
}
